<template>
  <div class="table-card">
    <div class="table-wrapper">
      <table class="custom-table">
        <thead>
          <tr>
            <th>USER</th>
            <th>EMAIL</th>
            <th>ROLE</th>
            <th>LOGINS</th>
            <th>LAST ACTIVE</th>
            <th class="text-right">ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          <!-- Loading State -->
          <tr v-if="loading && users.length === 0">
            <td colspan="6" class="text-center padding-3">
              <p class="status-msg">Fetching user directory...</p>
            </td>
          </tr>

          <!-- Search Empty State -->
          <tr v-else-if="!loading && users.length === 0 && searchQuery">
            <td colspan="6" class="text-center padding-3">
              <div class="empty-search-state">
                <p class="status-msg">No users found matching "<strong>{{ searchQuery }}</strong>"</p>
                <button @click="$emit('clear-search')" class="btn-clear-filter">Clear search filter</button>
              </div>
            </td>
          </tr>

          <!-- General Empty State -->
          <tr v-else-if="!loading && users.length === 0">
            <td colspan="6" class="text-center padding-3">
              <p class="status-msg">No users found in database.</p>
            </td>
          </tr>

          <!-- User Rows -->
          <tr v-for="user in users" :key="user.id">
            <td class="user-cell">
              <div class="user-info-group">
                <div class="avatar-wrapper">
                  <img v-if="user.picture" :src="user.picture" class="user-avatar" />
                  <div v-else class="avatar-fallback">{{ getInitials(user.name) }}</div>
                </div>
                <div class="text-content">
                  <span class="user-name">{{ user.firstname }} {{ user.lastname }}</span>
                  <span class="mobile-email">{{ user.email }}</span>
                </div>
              </div>
              <span class="role-badge mobile-role-badge" :class="user.role.toLowerCase()">{{ user.role }}</span>
            </td>

            <td class="email-cell user-email">{{ user.email }}</td>

            <td class="role-cell">
              <span class="role-badge" :class="user.role.toLowerCase()">{{ user.role }}</span>
            </td>

            <td class="logins-cell">
              <span class="login-chip">{{ user.logins }}</span>
            </td>

            <td class="activity-cell">
              <div class="activity-inner">
                <span class="status-dot" :class="{ active: isToday(user.lastLogin) }"></span>
                <span class="date-cell">{{ formatDate(user.lastLogin) }}</span>
              </div>
            </td>

            <td class="actions-cell">
              <button class="btn-edit-link" @click="$emit('edit', user)">Edit</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
defineProps({
  users: {
    type: Array,
    required: true
  },
  loading: Boolean,
  searchQuery: String
});

defineEmits(['edit', 'clear-search']);

const getInitials = (name) => {
  if (!name) return 'U';
  const parts = name.trim().split(' ');
  return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : name.slice(0, 2).toUpperCase();
};

const isToday = (dateString) => {
  if (!dateString) return false;
  return dateString.slice(0, 10) === new Date().toISOString().slice(0, 10);
};

const formatDate = (d) => {
  if (!d) return 'Never';
  const date = new Date(d);
  return isNaN(date.getTime()) ? d : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
</script>

<style lang="scss" scoped>
@use "@/assets/styles/abstract/variables" as *;

$border-dark: rgba(255, 255, 255, 0.08);
$accent-cyan: #38bdf8;
$text-light: #f8fafc;
$text-muted: #94a3b8;

.table-card {
  background: rgba(15, 23, 42, 0.6);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  border: 1px solid $border-dark;
  overflow: hidden;

  .table-wrapper {
    overflow-x: auto;

    .custom-table {
      width: 100%;
      border-collapse: collapse;
      text-align: left;
      background: transparent;
      border: none;

      thead {
        border: none;

        th {
          padding: 1rem 1.25rem;
          font-size: 0.7rem;
          font-weight: 700;
          color: $text-muted;
          letter-spacing: 0.08em;
          border-bottom: 2px solid rgba(255, 255, 255, 0.06);
          background: rgba(15, 23, 42, 0.8);
        }
      }

      tbody {
        background: transparent;
        border: none;

        tr {
          transition: all 0.15s ease;
          position: relative;
          background: transparent;
          border: none;

          td {
            padding: 0.9rem 1.25rem;
            vertical-align: middle;
            font-size: 0.88rem;
            border-bottom: 1px solid rgba(255, 255, 255, 0.04);
            color: $text-light;
          }

          &:last-child td {
            border-bottom: none;
          }

          &:hover {
            background-color: rgba(30, 41, 59, 0.6);
            box-shadow: inset 3px 0 0 $accent-cyan;
          }
        }
      }

      .user-cell {
        display: table-cell;
        vertical-align: middle;

        .user-info-group {
          display: flex;
          align-items: center;
          gap: 0.85rem;

          .avatar-wrapper {
            width: 38px;
            height: 38px;
            min-width: 38px;
            min-height: 38px;
            border-radius: 50%;
            overflow: hidden;
            flex-shrink: 0;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
            border: 2px solid rgba(255, 255, 255, 0.15);

            .user-avatar {
              width: 100%;
              height: 100%;
              object-fit: cover;
              display: block;
            }

            .avatar-fallback {
              width: 100%;
              height: 100%;
              color: #ffffff;
              font-weight: 700;
              font-size: 0.75rem;
              display: flex;
              align-items: center;
              justify-content: center;
            }
          }

          .text-content {
            display: flex;
            flex-direction: column;

            .user-name {
              font-weight: 700;
              color: $text-light;
              display: block;
            }

            .mobile-email {
              display: none;
            }
          }
        }

        .mobile-role-badge {
          display: none;
        }
      }

      .role-badge {
        display: inline-block;
        font-size: 0.7rem;
        font-weight: 700;
        padding: 0.25rem 0.75rem;
        border-radius: 50rem;
        background: rgba(255, 255, 255, 0.05);
        color: $text-muted;
        border: 1px solid rgba(255, 255, 255, 0.1);
        text-transform: uppercase;
        letter-spacing: 0.025em;

        &.admin {
          background: rgba(245, 158, 11, 0.15);
          color: #fbbf24;
          border-color: rgba(245, 158, 11, 0.3);
        }

        &.user {
          background: rgba(56, 189, 248, 0.15);
          color: #38bdf8;
          border-color: rgba(56, 189, 248, 0.3);
        }
      }

      .user-email {
        color: $text-muted;
        font-weight: 400;
      }

      .login-chip {
        background: rgba(255, 255, 255, 0.06);
        padding: 0.15rem 0.5rem;
        border-radius: 4px;
        font-weight: 600;
        color: $text-light;
      }

      .activity-cell {
        vertical-align: middle;

        .activity-inner {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;

          .status-dot {
            width: 7px;
            height: 7px;
            min-width: 7px;
            min-height: 7px;
            border-radius: 50%;
            background: #475569;

            &.active {
              background: #34d399;
              box-shadow: 0 0 8px rgba(52, 211, 153, 0.8);
            }
          }

          .date-cell {
            color: $text-muted;
            font-size: 0.85rem;
            white-space: nowrap;
          }
        }
      }

      .btn-edit-link {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        color: $accent-cyan;
        font-size: 0.8rem;
        font-weight: 600;
        padding: 0.3rem 0.75rem;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.15s ease;

        &:hover {
          background: $accent-cyan;
          color: #0f172a;
          border-color: $accent-cyan;
        }
      }

      .status-msg {
        color: $text-muted;
        margin: 0;
        padding: 1rem 0;
      }

      .empty-search-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;
        padding: 1rem 0;

        .btn-clear-filter {
          background: none;
          border: none;
          color: $accent-cyan;
          font-size: 0.82rem;
          font-weight: 600;
          cursor: pointer;
          text-decoration: underline;
        }
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .custom-table thead {
    display: none;
  }

  .custom-table,
  .custom-table tbody,
  .custom-table tr,
  .custom-table td {
    display: block;
    width: 100%;
  }

  .custom-table tr {
    margin-bottom: 1rem;
    background: rgba(15, 23, 42, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .custom-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    border: none;
    text-align: right;
    font-size: 0.88rem;
  }

  .custom-table td.email-cell,
  .custom-table td.role-cell {
    display: none !important;
  }

  .custom-table td.user-cell {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    padding-bottom: 0.75rem;

    .user-info-group {
      display: flex;
      align-items: center;
      gap: 0.85rem;

      .text-content {
        text-align: left;

        .user-name {
          font-weight: 700;
          color: $text-light;
          line-height: 1.2;
        }

        .mobile-email {
          display: block !important;
          font-size: 0.75rem;
          color: $text-muted;
          margin-top: 0.15rem;
        }
      }
    }

    .mobile-role-badge {
      display: inline-block !important;
    }
  }

  .custom-table td.logins-cell::before {
    content: "Total Logins";
    color: $text-muted;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
  }

  .custom-table td.activity-cell::before {
    content: "Last Login";
    color: $text-muted;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
  }

  .custom-table td.actions-cell {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    padding-top: 0.75rem;
    justify-content: center;

    .btn-edit-link {
      width: 140px;
      text-align: center;
      padding: 0.45rem;
    }
  }
}
</style>